import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { modeDemonstrationActif } from '../../api/demo.js';
import { cheminConditions } from '../../auth/cgu.js';
import styles from './Footer.module.css';

/**
 * `v{version}` seul, ou `v{version} · {sha}` quand le SHA a pu être lu au
 * build (`vite.config.ts`, § 8, `shaGitCourte`). Un dépôt construit hors de
 * `git`, ou un `git` absent de l'image, rend `'inconnu'` : l'afficher tel
 * quel disait « v0.0.0 · inconnu » (audit du 26 septembre 2026, § 6), soit
 * une valeur qui a l'air d'un bogue plutôt que d'un simple SHA manquant.
 * Fonction pure et exportée : le SHA vient d'un `define` remplacé au build,
 * jamais d'une vraie variable globale, donc c'est ELLE qu'on éprouve, pas
 * `__GIT_SHA__`.
 */
export function libelleVersion(version: string, sha: string): string {
  return sha === 'inconnu' ? `v${version}` : `v${version} · ${sha}`;
}

/**
 * Pied de page Halo (3.2) : discret, sous le contenu, pas de navigation
 * redondante. `__APP_VERSION__`/`__GIT_SHA__` viennent de `vite.config.ts`
 * (`define`), pas de `@artosera/ui` : c'est le paquet de la régie qui est
 * servi, pas celui du système de composants.
 */
export function Footer(): JSX.Element {
  const { t, i18n } = useTranslation('regie');
  const { pathname, search } = useLocation();
  const apercuPublic = import.meta.env.BASE_URL === '/templates/demo/';
  return (
    <footer className={styles.pied}>
      <span>
        {t('coquille.piedDePage')}
        {modeDemonstrationActif() ? <> · {t('coquille.donneesFictives')}</> : null}
      </span>
      <span className={styles.droite}>
        {/* Les CGU acceptées restent consultables depuis la régie (ARTO-59). */}
        <Link className={styles.lien} to={cheminConditions(`${pathname}${search}`)}>
          {t('auth.conditions.lien')}
        </Link>
        {apercuPublic ? (
          <a className={styles.retourThemes} href="/templates/">
            {i18n.language === 'en' ? 'Back to themes' : 'Retour aux thèmes'}
          </a>
        ) : (
          <span className={styles.version}>{libelleVersion(__APP_VERSION__, __GIT_SHA__)}</span>
        )}
      </span>
    </footer>
  );
}
